import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  category: string;
  fighter?: string;
  image: string;
  isNew?: boolean;
  isSoldOut?: boolean;
  isLimited?: boolean;
  badge?: string;
  colors?: string[];
}
interface FightCardProductProps {
  product: Product;
  onAddToCart: (product: Product) => void;
}
export function FightCardProduct({
  product,
  onAddToCart
}: FightCardProductProps) {
  const [selectedColor, setSelectedColor] = useState<string | null>(product.colors?.[0] ?? null);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} whileHover="hover" className="group relative bg-fight-dark border border-white/5 overflow-hidden flex flex-col h-full" data-id="element-65">
      {/* Image Container */}
      <div className="relative aspect-[3/4] overflow-hidden bg-fight-surface" data-id="element-66">
        <motion.img variants={{
        hover: {
          scale: 1.05
        }
      }} transition={{
        duration: 0.4
      }} src={product.image} alt={product.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" data-id="element-67" />
        <div className="absolute inset-0 bg-gradient-to-t from-fight-black via-transparent to-transparent opacity-60" data-id="element-68" />

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1" data-id="element-69">
          {product.isNew && <span className="bg-white text-black text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider" data-id="element-70">
              New
            </span>}
          {product.isLimited && <span className="text-black text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider animate-pulse" style={{
          backgroundColor: '#f5e614'
        }} data-id="element-71">
              Limited
            </span>}
          {product.isSoldOut && <span className="bg-gray-800 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider" data-id="element-72">
              Sold Out
            </span>}
        </div>

        {/* Category Tag */}
        <div className="absolute top-2 right-2" data-id="element-73">
          <span className="text-[10px] font-barlow font-bold tracking-widest text-gray-400 uppercase bg-black/50 backdrop-blur-sm px-1.5 py-0.5 border border-white/10" data-id="element-74">
            {product.category}
          </span>
        </div>

        {/* Quick Add Button */}
        <motion.button variants={{
        hover: {
          y: 0,
          opacity: 1
        }
      }} initial={{
        y: 20,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => !product.isSoldOut && onAddToCart(product)} disabled={product.isSoldOut} className="absolute bottom-0 left-0 right-0 bg-fight-red text-white py-2.5 font-bebas tracking-wider text-base flex items-center justify-center gap-1 hover:bg-fight-red-dark transition-colors disabled:bg-gray-800 disabled:cursor-not-allowed" data-id="element-75">
          {product.isSoldOut ? 'SOLD OUT' : <>
              <Plus className="w-3 h-3" data-id="element-76" /> QUICK ADD
            </>}
        </motion.button>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col flex-grow relative" data-id="element-77">
        {/* Hover Border Glow */}
        <motion.div variants={{
        hover: {
          opacity: 1
        }
      }} initial={{
        opacity: 0
      }} className="absolute inset-0 border border-fight-red/50 pointer-events-none z-10 box-border" style={{
        boxShadow: 'inset 0 0 20px rgba(220, 38, 38, 0.1)'
      }} data-id="element-78" />

        {product.fighter && <p className="text-[10px] text-fight-red font-bold tracking-widest uppercase mb-0.5" data-id="element-79">
            {product.fighter} Collection
          </p>}

        <h3 className="text-base md:text-xl font-bebas tracking-wide text-white group-hover:text-fight-red transition-colors duration-300 leading-tight mb-2" data-id="element-80">
          {product.name}
        </h3>

        {/* Color Swatches */}
        {product.colors && product.colors.length > 0 && <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-id="element-81">
            {product.colors.map(color => <button key={color} onClick={() => setSelectedColor(color)} aria-label={`Select color ${color}`} className="w-4 h-4 rounded-full border-2 transition-all duration-150 flex-shrink-0" style={{
          backgroundColor: color,
          borderColor: selectedColor === color ? '#f5e614' : 'rgba(255,255,255,0.2)',
          transform: selectedColor === color ? 'scale(1.3)' : 'scale(1)'
        }} data-id="element-82" />)}
          </div>}

        <div className="mt-auto flex items-baseline gap-2" data-id="element-83">
          <span className={`text-base font-barlow font-bold ${product.originalPrice ? 'text-fight-red' : 'text-white'}`} data-id="element-84">
            ${product.price}
          </span>
          {product.originalPrice && <span className="text-xs font-barlow text-gray-500 line-through" data-id="element-85">
              ${product.originalPrice}
            </span>}
        </div>
      </div>
    </motion.div>;
}